import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, AlertTriangle, XCircle, Package, MapPin, FileDown } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "../contexts/CartContext";
import { useI18n } from "../contexts/I18nContext";
import OrderItemCard from "../components/OrderItemCard";
import { downloadProductionSheet } from "../lib/downloadProductionSheet";
import { trackEvent } from "../hooks/useAnalytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Page de retour Stripe Checkout.
 * - Lit `session_id` depuis l'URL
 * - Poll /api/checkout/status/{session_id} toutes les 2s, max 5 essais
 * - Vide le panier sur succès (idempotent — guard par sessionStorage)
 */
const POLL_INTERVAL_MS = 2000;
const MAX_ATTEMPTS = 8;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clear } = useCart();
  const { lang } = useI18n();
  const [state, setState] = useState({ status: "polling", payment_status: null, data: null, error: null });
  const [downloading, setDownloading] = useState(false);
  const attemptsRef = useRef(0);
  const clearedRef = useRef(false);

  const handleDownloadSheet = async () => {
    if (downloading) return;
    const data = state.data || {};
    const md = data.metadata || {};
    setDownloading(true);
    try {
      await downloadProductionSheet({
        items: data.items || [],
        customer: {
          name: md.customer_name || "",
          email: data.customer_email || md.customer_email || "",
          phone: md.customer_phone || "",
          company: md.customer_company || "",
          address: md.shipping_address || "",
          notes: md.notes || "",
        },
        orderId: sessionId,
        paid: true,
        lang,
      });
      toast.success(
        lang === "fr"
          ? "Fiche de production téléchargée"
          : "Production sheet downloaded",
      );
    } catch (e) {
      toast.error(
        lang === "fr"
          ? "Erreur lors de la génération du PDF"
          : "PDF generation error",
      );
    } finally {
      setDownloading(false);
    }
  };

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "error", error: "Session manquante" });
      return;
    }
    let cancelled = false;
    const poll = async () => {
      try {
        const r = await axios.get(`${API}/checkout/status/${sessionId}`);
        if (cancelled) return;
        const d = r.data;
        if (d.payment_status === "paid") {
          setState({ status: "paid", payment_status: "paid", data: d });
          // Vider le panier une seule fois (anti double-fire React strict mode)
          if (!clearedRef.current) {
            clearedRef.current = true;
            try {
              const key = `signapharma-cleared-${sessionId}`;
              if (!sessionStorage.getItem(key)) {
                clear();
                // GA4 — événement `purchase` (clé pour les conversions e-commerce).
                // Idempotent : guard par sessionStorage (1 envoi par session_id).
                const total = (d.amount_total || 0) / 100;
                trackEvent("purchase", {
                  transaction_id: sessionId,
                  value: total,
                  currency: (d.currency || "chf").toUpperCase(),
                  items: (d.items || []).map((it) => ({
                    item_id: it.product_slug || "",
                    item_name:
                      typeof it.product_name === "object"
                        ? it.product_name.fr || it.product_slug
                        : it.product_name || it.product_slug,
                    quantity: it.quantity || 1,
                    price: it.unit_price_chf || 0,
                  })),
                });
                // Google Ads — conversion `purchase` (envoyée en parallèle de GA4)
                // Action de conversion "Achat" créée dans Google Ads.
                // send_to format : AW-{customerID}/{conversionLabel}
                trackEvent("conversion", {
                  send_to: "AW-18175334955/R5fDCLa1zbMcEKu01tpD",
                  transaction_id: sessionId,
                  value: total,
                  currency: (d.currency || "chf").toUpperCase(),
                });
                sessionStorage.setItem(key, "1");
              }
            } catch (e) { /* ignore */ }
          }
          return;
        }
        if (d.status === "expired") {
          setState({ status: "expired", payment_status: d.payment_status, data: d });
          return;
        }
        attemptsRef.current += 1;
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          setState({ status: "timeout", payment_status: d.payment_status, data: d });
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch (e) {
        if (cancelled) return;
        setState({ status: "error", error: e?.response?.data?.detail || e.message });
      }
    };
    poll();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const fmt = (n) => Math.round((n || 0) / 100).toLocaleString("fr-CH");

  if (state.status === "paid") {
    const data = state.data || {};
    const items = data.items || [];
    const md = data.metadata || {};
    return (
      <div className="max-w-3xl mx-auto px-6 py-20 lg:py-28" data-testid="payment-success">
        {/* Bandeau succès */}
        <div className="text-center mb-12">
          <CheckCircle2 size={72} className="text-[#39FF14] mx-auto mb-6 drop-shadow-[0_0_25px_rgba(57,255,20,0.5)]" />
          <h1 className="font-display text-4xl sm:text-5xl font-bold uppercase tracking-tight">
            <span className="chrome-text">
              {lang === "fr" ? "Paiement confirmé" : lang === "de" ? "Zahlung bestätigt" : lang === "it" ? "Pagamento confermato" : "Payment confirmed"}
            </span>
          </h1>
          <p className="mt-4 text-zinc-300 font-body max-w-xl mx-auto">
            {lang === "fr"
              ? "Merci ! Votre commande Signapharma est enregistrée et entre en production."
              : "Thank you! Your Signapharma order is registered and entering production."}
          </p>
        </div>

        {/* Numéro de commande + montant */}
        <div className="bg-[#080808] border border-[#39FF14]/30 p-6 mb-8" data-testid="payment-order-header">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1">
                {lang === "fr" ? "N° de commande" : "Order ID"}
              </div>
              <div className="font-mono text-sm text-white break-all">
                {sessionId?.slice(0, 24)}…
              </div>
              {data.invoice_number && (
                <div className="mt-2 pt-2 border-t border-white/10">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1">
                    {lang === "fr" ? "N° facture" : "Invoice #"}
                  </div>
                  <div
                    data-testid="payment-invoice-number"
                    className="font-mono text-sm text-[#39FF14] font-bold"
                  >
                    {data.invoice_number}
                  </div>
                </div>
              )}
            </div>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1">
                {lang === "fr" ? "Email de confirmation" : "Confirmation email"}
              </div>
              <div className="font-mono text-sm text-white truncate">
                {data.customer_email || md.customer_email || "—"}
              </div>
            </div>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1">
                {lang === "fr" ? "Total payé" : "Total paid"}
              </div>
              <div className="font-display text-2xl font-bold text-[#39FF14] tabular-nums neon-text-soft" data-testid="payment-amount">
                {fmt(data.amount_total)} CHF
              </div>
              {data.discount && data.discount.discount_amount_chf > 0 && (
                <div
                  data-testid="payment-discount-applied"
                  className="mt-1 font-mono text-[10px] uppercase tracking-widest text-[#39FF14]"
                >
                  {lang === "fr" ? "Remise volume" : "Volume discount"} −
                  {Math.round(data.discount.discount_rate * 100)}%
                  {" ("}−{Math.round(data.discount.discount_amount_chf).toLocaleString("fr-CH")} CHF{")"}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Visuel complet des produits configurés */}
        {items.length > 0 && (
          <div className="bg-[#080808] border border-white/5 p-6 mb-8" data-testid="payment-items">
            <h2 className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-zinc-400 mb-4">
              <Package size={14} className="text-[#39FF14]" />
              {lang === "fr" ? "Détail de votre commande" : "Your order details"}
              <span className="ml-auto text-zinc-500">
                {items.length} {items.length > 1
                  ? (lang === "fr" ? "produits" : "items")
                  : (lang === "fr" ? "produit" : "item")}
              </span>
            </h2>
            <div className="divide-y divide-white/5">
              {items.map((it, idx) => (
                <OrderItemCard key={idx} item={it} lang={lang} />
              ))}
            </div>
          </div>
        )}

        {/* Adresse de livraison + notes */}
        {(md.shipping_address || md.customer_name || md.customer_phone) && (
          <div className="bg-[#080808] border border-white/5 p-6 mb-8" data-testid="payment-shipping">
            <h2 className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-zinc-400 mb-4">
              <MapPin size={14} className="text-[#39FF14]" />
              {lang === "fr" ? "Livraison & contact" : "Shipping & contact"}
            </h2>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 text-sm">
              {md.customer_name && (
                <div>
                  <dt className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{lang === "fr" ? "Destinataire" : "Recipient"}</dt>
                  <dd className="text-white font-body">{md.customer_name}</dd>
                </div>
              )}
              {md.customer_phone && (
                <div>
                  <dt className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{lang === "fr" ? "Téléphone" : "Phone"}</dt>
                  <dd className="text-white font-body">{md.customer_phone}</dd>
                </div>
              )}
              {md.customer_company && (
                <div>
                  <dt className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{lang === "fr" ? "Société" : "Company"}</dt>
                  <dd className="text-white font-body">{md.customer_company}</dd>
                </div>
              )}
              {md.shipping_address && (
                <div className="sm:col-span-2">
                  <dt className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{lang === "fr" ? "Adresse" : "Address"}</dt>
                  <dd className="text-white font-body">{md.shipping_address}</dd>
                </div>
              )}
              {md.notes && (
                <div className="sm:col-span-2">
                  <dt className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{lang === "fr" ? "Notes" : "Notes"}</dt>
                  <dd className="text-zinc-300 font-body italic">« {md.notes} »</dd>
                </div>
              )}
            </dl>
          </div>
        )}

        {/* Prochaines étapes */}
        <div className="bg-[#080808] border border-white/5 p-6 mb-8">
          <h2 className="font-mono text-xs uppercase tracking-widest text-zinc-400 mb-3">
            {lang === "fr" ? "Prochaines étapes" : "Next steps"}
          </h2>
          <ol className="space-y-2 text-sm text-zinc-300 font-body list-decimal list-inside marker:text-[#39FF14]">
            <li>{lang === "fr"
              ? "Un email de confirmation contenant la fiche technique de production ET votre facture officielle (PDF) vient de vous être envoyé."
              : "A confirmation email with the production technical sheet AND your official invoice (PDF) has just been sent to you."}</li>
            <li>{lang === "fr"
              ? "Vous recevrez sous 24h ouvrées une maquette de production à valider par email."
              : "You will receive a production mockup within 24 business hours."}</li>
            <li>{lang === "fr"
              ? "Fabrication & livraison en 3 à 5 semaines, partout en Suisse."
              : "Manufacturing & delivery within 3 to 5 weeks, across Switzerland."}</li>
            <li>{lang === "fr"
              ? "Pose à prévoir séparément par un professionnel qualifié."
              : "Installation by a qualified professional must be arranged separately."}</li>
          </ol>
        </div>

        {/* CTAs */}
        <div className="flex flex-wrap justify-center gap-4">
          <button
            type="button"
            onClick={handleDownloadSheet}
            disabled={downloading}
            className="btn-neon disabled:opacity-50"
            data-testid="payment-download-sheet-button"
          >
            {downloading ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <FileDown size={16} />
            )}
            {downloading
              ? (lang === "fr" ? "Génération…" : "Generating…")
              : (lang === "fr"
                  ? "Télécharger fiche production (PDF)"
                  : lang === "de"
                  ? "Produktionsblatt (PDF)"
                  : lang === "it"
                  ? "Scheda produzione (PDF)"
                  : "Download production sheet (PDF)")}
          </button>
          <a
            href={`${API}/invoice/${sessionId}`}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-ghost-neon"
            data-testid="payment-download-invoice-button"
          >
            <FileDown size={16} />
            {lang === "fr"
              ? "Télécharger la facture (PDF)"
              : lang === "de"
              ? "Rechnung herunterladen (PDF)"
              : lang === "it"
              ? "Scarica fattura (PDF)"
              : "Download invoice (PDF)"}
          </a>
          <Link to="/" className="btn-ghost-neon" data-testid="payment-back-home">
            {lang === "fr" ? "Retour à l'accueil" : "Back to home"}
          </Link>
          <Link to="/produits" className="btn-ghost-neon">
            {lang === "fr" ? "Continuer les achats" : "Continue shopping"}
          </Link>
        </div>
      </div>
    );
  }

  if (state.status === "polling") {
    return (
      <div className="max-w-2xl mx-auto px-6 py-32 text-center" data-testid="payment-polling">
        <Loader2 size={56} className="text-[#39FF14] mx-auto mb-6 animate-spin" />
        <h1 className="font-display text-3xl font-bold uppercase">
          {lang === "fr" ? "Vérification du paiement…" : "Verifying payment…"}
        </h1>
        <p className="mt-4 text-zinc-400 font-body">
          {lang === "fr" ? "Merci de patienter quelques secondes." : "Please wait a moment."}
        </p>
      </div>
    );
  }

  if (state.status === "expired" || state.status === "timeout") {
    return (
      <div className="max-w-2xl mx-auto px-6 py-32 text-center" data-testid="payment-expired">
        <AlertTriangle size={56} className="text-yellow-400 mx-auto mb-6" />
        <h1 className="font-display text-3xl font-bold uppercase text-yellow-400">
          {state.status === "expired"
            ? (lang === "fr" ? "Session expirée" : "Session expired")
            : (lang === "fr" ? "Vérification longue" : "Slow verification")}
        </h1>
        <p className="mt-4 text-zinc-300 font-body">
          {lang === "fr"
            ? "Le paiement n'a pas pu être confirmé. Vérifiez votre boîte mail ou réessayez."
            : "Payment could not be confirmed. Please check your inbox or try again."}
        </p>
        <Link to="/panier" className="btn-neon mt-8 inline-flex">
          {lang === "fr" ? "Retour au panier" : "Back to cart"}
        </Link>
      </div>
    );
  }

  // status === "error"
  return (
    <div className="max-w-2xl mx-auto px-6 py-32 text-center" data-testid="payment-error">
      <XCircle size={56} className="text-red-400 mx-auto mb-6" />
      <h1 className="font-display text-3xl font-bold uppercase text-red-400">
        {lang === "fr" ? "Erreur de paiement" : "Payment error"}
      </h1>
      <p className="mt-4 text-zinc-400 font-body">{state.error}</p>
      <Link to="/panier" className="btn-neon mt-8 inline-flex">
        {lang === "fr" ? "Retour au panier" : "Back to cart"}
      </Link>
    </div>
  );
}
